import { Jwt, JwtRole, UserRole } from '@thingslog/repositories';
import { submitSessionLogout } from '@thingslog/utils';
import { getSessionUser, clearSessionUser } from './sessionUser';

/**
 * Reads the user that the backend session resolved for us (GET /api/me). The name is kept for the
 * call sites - there is no token in the browser any more.
 */
export default class JwtValidator {
  public decodedToken: Jwt | null;
  public isValidToken: boolean;

  public constructor(user?: Jwt | null) {
    this.decodedToken = user ?? getSessionUser();
    this.isValidToken = this.decodedToken !== null;
  }

  // #region public methods
  public hasRole = (userRole: UserRole): boolean => {
    if (!this.decodedToken) return false;
    let hasRole = false;

    try {
      this.decodedToken.roles.forEach((role: JwtRole) => {
        if (role.authority === userRole) {
          hasRole = true;
        }
      });
    } catch {
      return false;
    }

    return hasRole;
  };

  public hasRoles = (userRoles: UserRole[]): boolean => {
    if (!this.decodedToken) return false;
    let hasRole = false;

    try {
      this.decodedToken.roles.forEach((role: JwtRole) => {
        if (userRoles.includes(role.authority)) {
          hasRole = true;
        }
      });
    } catch {
      return false;
    }

    return hasRole;
  };

  // returns false if Superadmin has not selected company, otherwise always returns true
  public isAllowedUnlessSuperAdminWithoutSelectedCompany = (
    companyIdFromRedux: number | null
  ): boolean => {
    if (this.hasRole('ROLE_SUPER_ADMIN')) {
      return companyIdFromRedux ? true : false;
    }

    return true;
  };

  // returns companyId from redux if there is selected company or from the session user if not.
  // Note: its slightly different from useDetermineCompanyId as this Returns null for Superadmin
  public determineCompanyIdForUserRole = (companyIdFromRedux: number | null): number | null => {
    if (!this.decodedToken) return null;
    if (this.hasRole('ROLE_SUPER_ADMIN')) {
      return companyIdFromRedux ? companyIdFromRedux : null;
    } else if (this.hasRole('ROLE_ADMIN')) {
      return companyIdFromRedux ? companyIdFromRedux : Number(this.decodedToken?.companyId);
    } else {
      return Number(this.decodedToken.companyId);
    }
  };

  public logout = (): void => {
    clearSessionUser();
    localStorage.removeItem('selectedCompany');
    submitSessionLogout();
  };
  // #endregion
}
